import PrejoiningPage from "../components/Recruitment/Prejoining/PrejoiningPage";
import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import ProtectedLayout from "../components/Common/ProtectedLayout";
import MedicalCertificate from "../components/Leaves/MedicalCertificate";

import DevQuickLogin from "../components/DevQuickLogin";
import Login from "../components/Login";
import HomePage from "../components/HomePage";
import BaseLayout from "../Layout/BaseLayout";
import NotFound from "../components/NotFound";
import MaintenancePage from "../components/Maintenance/MaintenancePage";
import EmployeeProjectView from "../components/ProjectManagement/Employees/EmployeeProjectView";
import OfficeSettings from "../components/Settings/OfficeSettings";
import OfficeList from "../components/Settings/OfficeList";
import DepartmentSettings from "../components/Settings/DepartmentSettings";
import UserSettings from "../components/Settings/UserSettings";
import UserEdit from "../components/Settings/UserEdit";
import CompanySettings from "../components/Settings/CompanySettings";
import NotificationPreferences from "../components/Settings/NotificationPreferences";
import PMStyleGuide from "../components/ProjectManagement/StyleGuide";
import RedirectOldProfileRoute from "../components/Profile/RedirectOldProfileRoute";
import XeroRedirect from "../components/Xero/XeroRedirect";
import ProtectedRoute from "../components/Common/ProtectedRoute";
import SentryPrismaScanPage from "../components/Admin/SentryPrismaScanner/SentryPrismaScanPage";
import { ROLES } from "../service/RoleService";
import SurveyFormPage from "../components/Survey/SurveyFormPage";
import ExternalSurveyPage from "../components/Survey/ExternalSurveyPage";
import AnonymousSurveyPage from "../components/Survey/AnonymousSurveyPage";

// Client Portal
import { ClientAuthProvider } from "../context/ClientAuthContext";
import ClientLogin from "../components/ClientPortal/ClientLogin";
import ClientProtectedLayout from "../components/ClientPortal/ClientProtectedLayout";
import ClientPortalLayout from "../components/ClientPortal/ClientPortalLayout";
import ClientDashboard from "../components/ClientPortal/Dashboard/ClientDashboard";
import ClientProjectDetail from "../components/ClientPortal/Projects/ClientProjectDetail";
import ClientFeedbackForm from "../components/ClientPortal/Projects/ClientFeedbackForm";
import ClientProfile from "../components/ClientPortal/Profile/ClientProfile";
import ClientCompanyList from "../components/ClientPortal/Company/ClientCompanyList";
import ClientCompanyDetail from "../components/ClientPortal/Company/ClientCompanyDetail";

const DevLogin = lazy(() => import("../components/DevLogin"));
const SurveyInbox = lazy(() => import("../components/Survey/SurveyInbox"));

function RedirectWithId({ to }) {
    const { id } = useParams();
    const separator = to.includes('?') ? '&' : '?';
    return <Navigate to={`${to}${separator}id=${id}`} replace />;
}

function RedirectWithPath({ to }) {
    const { id } = useParams();
    return <Navigate to={`${to}/${id}`} replace />;
}

function RedirectWithPreservedQuery({ to }) {
    const { search } = useLocation();
    const query = search.startsWith('?') ? search.slice(1) : search;
    const separator = query && to.includes('?') ? '&' : query ? '?' : '';
    return <Navigate to={`${to}${separator}${query}`} replace />;
}

function AppRouter() {
    return (
        <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/pre-joining" element={<PrejoiningPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/external-survey/:token" element={<ExternalSurveyPage />} />
            <Route path="/external-survey-preview/:surveyId" element={<ExternalSurveyPage previewMode />} />
            <Route path="/anonymous-survey" element={<AnonymousSurveyPage />} />
            <Route
                path="/anonymous-survey-preview/:surveyId"
                element={(
                    <ProtectedRoute roles={[ROLES.ROLE_HR]}>
                        <AnonymousSurveyPage previewMode />
                    </ProtectedRoute>
                )}
            />
            {(process.env.NODE_ENV !== "production" || import.meta.env.VITE_CLOUD_PREVIEW === 'true') && (
                <>
                    <Route
                        path="/dev-login"
                        element={(
                            <Suspense fallback={null}>
                                <DevLogin />
                            </Suspense>
                        )}
                    />
                    <Route path="/dev-quick-login" element={<DevQuickLogin />} />
                </>
            )}

            {/* Protected routes - all automatically require authentication */}
            <Route path="/" element={<ProtectedLayout />}>
                <Route index element={<HomePage />} />
                <Route path="profile" element={<HomePage />} />
                <Route path="profile/:userId" element={<HomePage />} />
                {/* Backward compatibility - redirect old route to new one */}
                <Route path="profile/user/:userId" element={<RedirectOldProfileRoute />} />

                <Route path="project-management" element={<HomePage />} />
                <Route
                    path="project-management/reports/override-integrity"
                    element={<RedirectWithPreservedQuery to="/finances?tab=reports&view=revenue-adjustments&residualReview=1" />}
                />
                <Route path="ai-tools" element={<HomePage />} />
                <Route path="knowledgebase" element={<HomePage />} />
                <Route path="knowledgebase/company-policies" element={<HomePage />} />
                <Route path="knowledgebase/company-policies/:policyKey" element={<HomePage />} />
                <Route path="knowledgebase/sustainability-cdp" element={<HomePage />} />
                <Route path="knowledgebase/:folderId" element={<HomePage />} />
                <Route path="analytics" element={<HomePage />} />

                {/* Company Directory */}
                <Route path="company-directory" element={<Navigate to="/company-directory/about" replace />} />
                <Route path="company-directory/about" element={<HomePage />} />
                <Route path="company-directory/office" element={<HomePage />} />
                <Route path="company-directory/team" element={<HomePage />} />
                <Route path="company-directory/department" element={<HomePage />} />
                <Route path="company-directory/organization" element={<HomePage />} />

                {/* Core features */}
                <Route path="timesheet" element={<HomePage />} />
                <Route path="calendar" element={<HomePage />} />
                <Route path="leaves" element={<HomePage />} />
                <Route path="leaves/requests/:id/medical-certificate" element={<MedicalCertificate />} />
                <Route path="appraisal" element={<HomePage />} />
                <Route path="appraisal/:id" element={<HomePage />} />
                <Route path="tasks" element={<HomePage />} />
                <Route path="tasks/*" element={<HomePage />} />
                <Route path="wiki/*" element={<HomePage />} />
                <Route path="finances" element={<HomePage />} />
                <Route path="finances/expense-requests/:id" element={<HomePage />} />
                <Route path="finances/vendor-bills/:id" element={<HomePage />} />
                <Route path="finances/sales-order/:id" element={<HomePage />} />
                <Route path="finances/invoice/:id" element={<HomePage />} />
                <Route path="newsfeed" element={<HomePage />} />
                <Route path="announcements" element={<HomePage />} />
                <Route path="notification" element={<HomePage />} />
                <Route path="notifications" element={<HomePage />} />
                <Route path="surveys" element={<HomePage />} />
                <Route path="surveys/:id" element={<HomePage />} />
                <Route path="workplace-feedback" element={<HomePage />} />
                <Route
                    path="survey-preview/:surveyId"
                    element={(
                        <HomePage>
                            <Suspense fallback={null}>
                                <SurveyInbox previewMode />
                            </Suspense>
                        </HomePage>
                    )}
                />
                <Route path="qa" element={<HomePage />} />

                {/* API Usage Analysis */}
                <Route path="admin/api-usage" element={<HomePage />} />
                <Route path="admin/api-usage/*" element={<HomePage />} />

                {/* Training */}
                <Route path="training" element={<HomePage />} />
                <Route path="course/:id" element={<HomePage />} />
                <Route path="course/:id/quiz" element={<HomePage />} />
                <Route path="course/:id/quiz/result" element={<HomePage />} />

                {/* Admin */}
                <Route path="admin" element={<HomePage />} />
                <Route path="admin/activity-log" element={<HomePage />} />
                <Route path="admin/vendor-bill-processing" element={<HomePage />} />
                <Route path="admin/aspire-invoice-allocation" element={<HomePage />} />
                <Route path="admin/tech-platform-vendor-bill-buffer" element={<HomePage />} />
                <Route path="admin/api-key-management" element={<HomePage />} />
                <Route path="admin/email-management" element={<HomePage />} />
                <Route path="admin/email-management/template/:id" element={<HomePage />} />
                <Route path="admin/ai-prompts" element={<HomePage />} />
                <Route path="admin/process-improvement" element={<HomePage />} />
                <Route path="admin/notifications" element={<HomePage />} />
                <Route path="admin/newsfeed" element={<HomePage />} />
                <Route path="admin/announcements" element={<HomePage />} />
                <Route path="admin/finances" element={<HomePage />} />
                <Route
                    path="admin/finances/settings"
                    element={<Navigate to="/admin/finances?tab=settings" replace />}
                />
                <Route
                    path="admin/finances/client-invoices"
                    element={<Navigate to="/finances?tab=client-invoices" replace />}
                />
                <Route
                    path="admin/finances/vendor-bills"
                    element={<Navigate to="/finances?tab=vendor-bills" replace />}
                />
                <Route
                    path="admin/finances/credit-notes"
                    element={<Navigate to="/finances?tab=credit-notes" replace />}
                />
                <Route path="admin/finances/expense-requests/:id" element={<HomePage />} />
                <Route path="admin/xero" element={<HomePage />} />
                <Route path="admin/hr/dashboard/detail" element={<HomePage />} />
                <Route path="admin/hr/dashboard" element={<HomePage />} />
                <Route path="admin/hr/recruitment" element={<HomePage />} />
                <Route path="admin/hr/appraisals" element={<HomePage />} />
                <Route path="admin/hr/appraisals/category/new" element={<HomePage />} />
                <Route path="admin/hr/appraisals/category/:categoryId/edit" element={<HomePage />} />
                <Route path="admin/hr/surveys" element={<HomePage />} />
                <Route path="admin/hr/surveys/new" element={<HomePage><SurveyFormPage /></HomePage>} />
                <Route path="admin/hr/surveys/:id/edit" element={<HomePage><SurveyFormPage /></HomePage>} />
                <Route path="admin/hr/surveys/:id" element={<HomePage />} />
                <Route path="admin/hr/workplace-feedback" element={<HomePage />} />
                <Route path="admin/hr/leaves" element={<HomePage />} />
                <Route path="admin/hr/asset" element={<HomePage />} />
                <Route path="admin/hr/trainings" element={<HomePage />} />
                <Route path="admin/hr/awards" element={<HomePage />} />
                <Route path="admin/hr/working-location" element={<HomePage />} />
                <Route path="admin/hr/working-location/new" element={<HomePage />} />
                <Route path="admin/hr/working-location/:id" element={<HomePage />} />
                <Route path="admin/knowledgebase" element={<HomePage />} />
                <Route path="admin/knowledgebase/policy-send/:policyKey" element={<HomePage />} />
                <Route path="admin/qna" element={<HomePage />} />
                <Route path="admin/user-impersonator" element={<HomePage />} />
                <Route path="admin/manage-account" element={<HomePage />} />

                {/* Project Management Detail Pages */}
                <Route path="project-management/client/:id" element={<HomePage />} />
                <Route path="project-management/contact/:id" element={<HomePage />} />
                <Route path="project-management/pic/:id" element={<HomePage />} />
                <Route path="project-management/vendor/:id" element={<HomePage />} />
                <Route path="project-management/project/:id" element={<HomePage />} />
                <Route path="project-management/project/view/:id" element={<RedirectWithPath to="/project-management/project" />} />
                <Route path="project-management/projects/:id" element={<RedirectWithPath to="/project-management/project" />} />
                <Route path="project-management/projects/detail/:id" element={<RedirectWithPath to="/project-management/project" />} />
                <Route path="project-management/feedback/template/:id" element={<HomePage />} />
                <Route path="project-management/invoice/:invoiceNo" element={<HomePage />} />

                {/* Chatbot */}
                <Route path="chatbot" element={<HomePage />} />
                <Route path="chatbot/section" element={<HomePage />} />
                <Route path="chatbot/section/:id" element={<HomePage />} />
                <Route path="chatbot/company-prompt" element={<HomePage />} />
                <Route path="chatbot/department-prompt" element={<HomePage />} />

                {/* WFA - user self-service page */}
                <Route path="wfa" element={<HomePage />} />
                <Route path="wfa/new" element={<HomePage />} />
                <Route path="wfa/:id" element={<HomePage />} />

                {/* Xero OAuth - redirects to Node.js API */}
                <Route path="xero" element={<XeroRedirect />} />

                {/* Office Settings — HR and above only */}
                <Route path="admin/office" element={<ProtectedRoute roles={[ROLES.ROLE_HR]}><HomePage><OfficeList /></HomePage></ProtectedRoute>} />
                <Route path="admin/office/new" element={<ProtectedRoute roles={[ROLES.ROLE_HR]}><HomePage><OfficeSettings /></HomePage></ProtectedRoute>} />
                <Route path="admin/office/:id" element={<ProtectedRoute roles={[ROLES.ROLE_HR]}><HomePage><OfficeSettings /></HomePage></ProtectedRoute>} />

                {/* Department Settings */}
                <Route path="admin/department" element={<HomePage><DepartmentSettings /></HomePage>} />

                {/* HR - User/Employee Management */}
                <Route path="admin/hr/employees" element={<HomePage><UserSettings /></HomePage>} />
                <Route path="admin/hr/employees/edit/:id" element={<HomePage><ProtectedRoute roles={[ROLES.ROLE_HR, ROLES.ROLE_MANAGEMENT, ROLES.ROLE_OWNER]} allowManager><UserEdit /></ProtectedRoute></HomePage>} />

                {/* Company Settings */}
                <Route path="admin/company" element={<HomePage><CompanySettings /></HomePage>} />

                {/* Email settings pages - routed through HomePage for sidebar */}
                <Route path="admin/email-banner" element={<HomePage />} />
                <Route path="admin/email-signature-template" element={<HomePage />} />
                <Route path="settings/email-banner" element={<Navigate to="/admin/email-banner" replace />} />
                <Route path="settings/email/banner" element={<Navigate to="/admin/email-banner" replace />} />
                <Route path="settings/email/signature-template" element={<Navigate to="/admin/email-signature-template" replace />} />

                {/* Personal Notification Preferences */}
                <Route
                    path="settings/notification-preferences"
                    element={<HomePage><NotificationPreferences /></HomePage>}
                />
            </Route>

            {/* Special routes outside protected layout */}
            <Route path="/pm-style-guide" element={<BaseLayout><PMStyleGuide /></BaseLayout>} />
            <Route path="/project-management/employee/:email" element={<BaseLayout><EmployeeProjectView /></BaseLayout>} />

            {/* Owner-only Sentry Prisma scanner */}
            <Route
                path="/ops/sentry-prisma-scan"
                element={(
                    <ProtectedRoute roles={[ROLES.ROLE_OWNER]}>
                        <HomePage>
                            <SentryPrismaScanPage />
                        </HomePage>
                    </ProtectedRoute>
                )}
            />
            <Route
                path="/ops/sentry-prisma-scan/data"
                element={(
                    <ProtectedRoute roles={[ROLES.ROLE_OWNER]}>
                        <HomePage>
                            <SentryPrismaScanPage showJson />
                        </HomePage>
                    </ProtectedRoute>
                )}
            />

            {/* Redirect old recruitment URLs to new format */}
            <Route path="/recruit" element={<Navigate to="/admin/hr/recruitment" replace />} />
            <Route path="/recruit/vacancy-detail/:id" element={<RedirectWithId to="/admin/hr/recruitment?type=vacancy-detail" />} />
            <Route path="/recruit/candidate/:id" element={<RedirectWithId to="/admin/hr/recruitment?type=candidate" />} />
            <Route path="/recruit/my-dashboard" element={<Navigate to="/admin/hr/recruitment?tab=my-dashboard" replace />} />
            <Route path="/recruit/talents" element={<Navigate to="/admin/hr/recruitment?tab=talents" replace />} />
            <Route path="/recruit/talent-detail/:id" element={<RedirectWithId to="/admin/hr/recruitment?tab=talents&type=talent-detail" />} />
            <Route path="/recruit/evaluation" element={<Navigate to="/admin/hr/recruitment?tab=evaluation" replace />} />
            <Route path="/recruit/evaluation/:id" element={<RedirectWithId to="/admin/hr/recruitment?tab=evaluation&type=evaluation-template" />} />
            <Route path="/recruit/screening-questions" element={<Navigate to="/admin/hr/recruitment?tab=screening-questions" replace />} />
            <Route path="/recruit/emails" element={<Navigate to="/admin/hr/recruitment?tab=emails" replace />} />
            <Route path="/recruit/activity" element={<Navigate to="/admin/hr/recruitment?tab=activity" replace />} />
            <Route path="/recruit/setting" element={<Navigate to="/admin/hr/recruitment?tab=setting" replace />} />
            <Route path="/recruit/ai-management" element={<Navigate to="/admin/hr/recruitment?tab=vacancies" replace />} />

            {/* Redirect old /checklist/* URLs to /tasks/* */}
            <Route path="/checklist" element={<Navigate to="/tasks/checklists" replace />} />
            <Route path="/checklist/templates" element={<Navigate to="/tasks/templates" replace />} />
            <Route path="/checklist/template/:id" element={<RedirectWithPath to="/tasks/template" />} />
            <Route path="/checklist/list" element={<Navigate to="/tasks/checklists" replace />} />
            <Route path="/checklist/my-task" element={<Navigate to="/tasks/my-tasks" replace />} />
            <Route path="/checklist/my-verification-needed" element={<Navigate to="/tasks/verification" replace />} />
            <Route path="/checklist/ai" element={<Navigate to="/tasks/ai" replace />} />
            <Route path="/checklist/:id" element={<RedirectWithPath to="/tasks/checklist" />} />



            {/* Moved to HomePage routing
            <Route
                path="/company-directory"
                element={<LayoutCompanyDirectory />}
            >
                <Route index element={<Navigate to="info" replace />} />
                <Route path="about" element={<AboutMediatropy />} />
                <Route path="office" element={<CompanyOffice />} />
                <Route path="department" element={<CompanyDepartment />} />
                <Route path="team" element={<CompanyTeam />} />
                <Route path="organization" element={<CompanyOrganization />} />
            </Route>
            */}

            {/* Client Portal — separate auth context */}
            <Route path="/client-section" element={<ClientAuthProvider><Outlet /></ClientAuthProvider>}>
                <Route path="login" element={<ClientLogin />} />
                <Route element={<ClientProtectedLayout />}>
                    <Route element={<ClientPortalLayout />}>
                        <Route index element={<ClientDashboard />} />
                        <Route path="project" element={<ClientDashboard />} />
                        <Route path="project/:id" element={<ClientProjectDetail />} />
                        <Route path="project/:id/feedback" element={<ClientFeedbackForm />} />
                        <Route path="profile" element={<ClientProfile />} />
                        <Route path="company" element={<ClientCompanyList />} />
                        <Route path="company/:id" element={<ClientCompanyDetail />} />
                    </Route>
                </Route>
            </Route>

            {/* 404 Not Found - This should be the last route */}
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

export default AppRouter;
